import styled from "@emotion/styled";
import * as React from "react";
import { useSelector } from "react-redux";

import { Check, Close } from "@livechat/design-system-icons";
import { Button, DesignToken, Heading, Icon, SpacingToken, Text } from "@livechat/design-system-react-components";

import { hasManuallyIncreasedLicensePlanLimits } from "helpers/subscription-plans";
import { selectStandardPlansLimits } from "store/entities/application-settings/selectors";
import { selectLicenseSettings } from "store/entities/license";
import { Plan } from "store/entities/subscriptions";
import { withVar } from "themes/helpers";

interface IProps {
  features: string[];
  isSamePlan: boolean;
  selectedPlan?: Plan;
  currentPlan?: Plan;
  downgrade?: boolean;
  className?: string;
}

const SubscriptionPlansFeatures: React.FC<IProps> = ({
  features,
  downgrade,
  className,
  isSamePlan,
  selectedPlan,
  currentPlan,
}) => {
  const licenseSettings = useSelector(selectLicenseSettings);
  const { team: standardTeamLimits } = useSelector(selectStandardPlansLimits);
  const hasIncreasedLimits = hasManuallyIncreasedLicensePlanLimits(licenseSettings, standardTeamLimits);

  const displayLoyalCustomerLabel = hasIncreasedLimits && selectedPlan === Plan.Team && currentPlan === Plan.Team;

  return (
    <FeaturesWrapper className={className}>
      <Heading
        size="xs"
        customColor={withVar(downgrade ? DesignToken.ContentBasicNegative : DesignToken.ContentBasicPrimary)}
        css={{ marginBottom: withVar(SpacingToken.Spacing1) }}
      >
        {isSamePlan ? "What you get:" : downgrade ? "You'll lose the option to:" : "What you'll get:"}
      </Heading>
      {features.map(feature => (
        <FeatureItem key={feature}>
          <Icon
            source={downgrade ? Close : Check}
            size="small"
            customColor={withVar(downgrade ? DesignToken.ActionNegativeDefault : DesignToken.ActionPositiveDefault)}
          />
          <Text>{feature}</Text>
        </FeatureItem>
      ))}
      <Button
        kind="link-light"
        href="https://www.helpdesk.com/pricing-legacy/#compare-all-plans"
        target="_blank"
        css={{ marginTop: withVar(SpacingToken.Spacing3) }}
      >
        See all features available in the plan
      </Button>
      {displayLoyalCustomerLabel && (
        <Text
          customColor={withVar(DesignToken.ContentBasicDisabled)}
          css={{ marginTop: withVar(SpacingToken.Spacing3) }}
        >
          As a loyal customer, you have access to higher feature limits beyond the current offerings.
        </Text>
      )}
    </FeaturesWrapper>
  );
};

const FeaturesWrapper = styled.div`
  display: flex;
  flex: 10;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  padding: 0 ${withVar(SpacingToken.Spacing8)};
  gap: ${withVar(SpacingToken.Spacing1)};
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${withVar(SpacingToken.Spacing2)};
`;

export { SubscriptionPlansFeatures, type IProps };
